import { X } from 'lucide-react';
import {
  BadgeDollarSign,
  Boxes,
  Package,
  ShoppingBag,
  ShoppingCart,
  Store,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/Sheet';
import { StatTile } from '@/components/marketing/MetricTile';
import { TrendChart } from '@/components/marketing/TrendChart';
import {
  ChannelStatusNotice,
  resolveChannelStatus,
} from '@/components/marketing/ChannelStatusNotice';
import { CHART_SERIES } from '@/lib/brand';
import { formatCurrency, formatDate } from '@/lib/formatters';
import { formatShortDay, formatTooltipDay } from '@/features/marketing/range';
import { cn } from '@/lib/utils';
import {
  useSlicedSales,
  type EcommerceKind,
  type SalesDashOrder,
  type SalesSkuRow,
} from '@/features/sales-marketing/hooks';
import {
  SALES_CHANNEL_FILTERS,
  type SalesChannelFilter,
  type SalesDashRange,
} from '@/features/sales-marketing/range';

interface SalesModuleProps {
  range: SalesDashRange;
  channel: SalesChannelFilter;
  selectedDay: string | null;
  selectedSkuKey: string | null;
  onChannelChange: (channel: SalesChannelFilter) => void;
  onDayChange: (day: string | null) => void;
  onSkuChange: (skuKey: string | null) => void;
  comparisonLabel: string;
}

export function SalesModule({
  range,
  channel,
  selectedDay,
  selectedSkuKey,
  onChannelChange,
  onDayChange,
  onSkuChange,
  comparisonLabel,
}: SalesModuleProps) {
  const { query, totals, previous, skus, skuOrders, selectedSku, deltaLabelOverride } =
    useSlicedSales(range, channel, selectedDay, selectedSkuKey);
  const data = query.data;
  const loading = query.isLoading;
  const deltaLabel = deltaLabelOverride ?? comparisonLabel;

  const status = resolveChannelStatus({
    isLoading: query.isLoading,
    error: query.error,
    connected: data?.connected,
    awaitingFirstSync: data?.awaitingFirstSync,
  });

  const showShopify = channel !== 'amazon';
  const showAmazon = channel !== 'shopify';

  const chartData = data?.daily ?? [];

  const series = [
    ...(showShopify
      ? [
          {
            key: 'shopifyRevenueCents',
            label: 'Shopify revenue',
            color: CHART_SERIES[0],
            kind: 'bar' as const,
            stackId: 'revenue',
            format: (value: number) => formatCurrency(value, { whole: true }),
          },
        ]
      : []),
    ...(showAmazon
      ? [
          {
            key: 'amazonRevenueCents',
            label: 'Amazon revenue',
            color: CHART_SERIES[4],
            kind: 'bar' as const,
            stackId: 'revenue',
            format: (value: number) => formatCurrency(value, { whole: true }),
          },
        ]
      : []),
    {
      key: 'orders',
      label: 'Orders',
      color: CHART_SERIES[3],
      kind: 'line' as const,
      axis: 'right' as const,
    },
  ];

  return (
    <section className="space-y-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h2 className="text-lg font-medium tracking-headline text-foreground">Sales</h2>
        </div>
        <div className="flex flex-wrap items-center gap-1">
          {SALES_CHANNEL_FILTERS.map((option) => (
            <Button
              key={option.id}
              size="sm"
              variant={channel === option.id ? 'default' : 'outline'}
              onClick={() => onChannelChange(option.id)}
            >
              {option.label}
            </Button>
          ))}
        </div>
      </div>

      <ChannelStatusNotice
        status={status}
        channel="Shopify / Amazon"
        error={query.error instanceof Error ? query.error.message : null}
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatTile
          label="Revenue"
          icon={BadgeDollarSign}
          loading={loading}
          value={formatCurrency(totals.revenueCents, { whole: true })}
          delta={pctSafe(totals.revenueCents, previous.revenueCents)}
          deltaLabel={deltaLabel}
          hint={selectedDay ? formatTooltipDay(selectedDay) : undefined}
        />
        <StatTile
          label="Orders"
          icon={ShoppingCart}
          loading={loading}
          value={totals.orders.toLocaleString()}
          delta={pctSafe(totals.orders, previous.orders)}
          deltaLabel={deltaLabel}
          hint={
            totals.cancelledOrders > 0
              ? `${totals.cancelledOrders.toLocaleString()} cancelled excluded`
              : undefined
          }
        />
        <StatTile
          label="Average order"
          icon={ShoppingBag}
          loading={loading}
          value={formatCurrency(totals.aovCents)}
          delta={pctSafe(totals.aovCents, previous.aovCents)}
          deltaLabel={deltaLabel}
        />
        <StatTile
          label="Units sold"
          icon={Package}
          loading={loading}
          value={totals.units.toLocaleString()}
          delta={pctSafe(totals.units, previous.units)}
          deltaLabel={deltaLabel}
          hint={
            (data?.ordersWithoutSkus ?? 0) > 0
              ? 'Amazon orders have no line items yet — units are Shopify-only'
              : undefined
          }
        />
      </div>

      {channel === 'all' && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <ChannelSplitCard
            label="Shopify"
            icon={Store}
            revenueCents={totals.shopifyRevenueCents}
            orders={totals.shopifyOrders}
            share={safeShare(totals.shopifyRevenueCents, totals.revenueCents)}
            loading={loading}
          />
          <ChannelSplitCard
            label="Amazon"
            icon={Boxes}
            revenueCents={totals.amazonRevenueCents}
            orders={totals.amazonOrders}
            share={safeShare(totals.amazonRevenueCents, totals.revenueCents)}
            loading={loading}
          />
        </div>
      )}

      <Card>
        <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3 space-y-0">
          <div>
            <CardTitle>Revenue by day</CardTitle>
          </div>
          {selectedDay && (
            <Button variant="outline" size="sm" onClick={() => onDayChange(null)}>
              {formatShortDay(selectedDay)}
              <X className="h-3.5 w-3.5" />
            </Button>
          )}
        </CardHeader>
        <CardContent>
          <TrendChart
            data={chartData}
            series={series}
            highlightedX={selectedDay}
            onPointClick={(day) => onDayChange(day === selectedDay ? null : day)}
            formatLeftAxis={(value) => formatCurrency(value, { compact: true, whole: true })}
            formatRightAxis={(value) => Math.round(value).toLocaleString()}
            height={320}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>By product SKU</CardTitle>
        </CardHeader>
        <CardContent>
          <SkuTable
            rows={skus}
            loading={loading}
            selectedKey={selectedSkuKey}
            onSelect={(key) => onSkuChange(key === selectedSkuKey ? null : key)}
            amazonOrdersWithoutSkus={
              selectedDay
                ? (data?.orders ?? []).filter(
                    (o) =>
                      o.day === selectedDay &&
                      o.channelKind === 'amazon' &&
                      o.lineItems.length === 0 &&
                      o.status !== 'cancelled',
                  ).length
                : data?.ordersWithoutSkus ?? 0
            }
          />
        </CardContent>
      </Card>

      <SkuOrdersSheet
        sku={selectedSku}
        orders={skuOrders}
        open={selectedSku !== null}
        onOpenChange={(open) => {
          if (!open) onSkuChange(null);
        }}
      />
    </section>
  );
}

function ChannelSplitCard({
  label,
  icon: Icon,
  revenueCents,
  orders,
  share,
  loading,
}: {
  label: string;
  icon: typeof Store;
  revenueCents: number;
  orders: number;
  share: number;
  loading: boolean;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-2xl font-medium tracking-headline text-muted-foreground/60">—</p>
        ) : (
          <>
            <p className="text-2xl font-medium tabular-nums tracking-headline text-foreground">
              {formatCurrency(revenueCents, { whole: true })}
            </p>
            <p className="mt-2 text-xs text-muted-foreground">
              {orders.toLocaleString()} orders · {(share * 100).toFixed(1)}% of window
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function SkuTable({
  rows,
  loading,
  selectedKey,
  onSelect,
  amazonOrdersWithoutSkus,
}: {
  rows: SalesSkuRow[];
  loading: boolean;
  selectedKey: string | null;
  onSelect: (key: string) => void;
  amazonOrdersWithoutSkus: number;
}) {
  if (loading) {
    return <p className="py-8 text-center text-sm text-muted-foreground">Loading SKUs…</p>;
  }

  if (rows.length === 0) {
    return (
      <div className="space-y-2 py-8 text-center text-sm text-muted-foreground">
        <p>No SKU-level line items in this window.</p>
        {amazonOrdersWithoutSkus > 0 && (
          <p>
            {amazonOrdersWithoutSkus.toLocaleString()} Amazon{' '}
            {amazonOrdersWithoutSkus === 1 ? 'order has' : 'orders have'} no
            line items yet — Amazon sync currently stores order totals only.
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {amazonOrdersWithoutSkus > 0 && (
        <p className="text-xs text-muted-foreground">
          {amazonOrdersWithoutSkus.toLocaleString()} Amazon{' '}
          {amazonOrdersWithoutSkus === 1 ? 'order is' : 'orders are'} counted in
          revenue but have no SKUs — Amazon sync does not yet pull order items.
        </p>
      )}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>SKU</TableHead>
            <TableHead>Product</TableHead>
            <TableHead>Channel</TableHead>
            <TableHead className="text-right">Units</TableHead>
            <TableHead className="text-right">Orders</TableHead>
            <TableHead className="text-right">Revenue</TableHead>
            <TableHead className="text-right">Share</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.slice(0, 50).map((row) => (
            <TableRow
              key={row.key}
              className={cn('cursor-pointer', selectedKey === row.key && 'bg-secondary')}
              onClick={() => onSelect(row.key)}
            >
              <TableCell className="font-medium tabular-nums">
                {row.sku === 'NO-SKU' ? '—' : row.sku}
              </TableCell>
              <TableCell className="max-w-[280px] truncate">{row.name}</TableCell>
              <TableCell>
                <ChannelKindBadge kind={row.channelKind} />
              </TableCell>
              <TableCell className="text-right tabular-nums">{row.units.toLocaleString()}</TableCell>
              <TableCell className="text-right tabular-nums">{row.orders.toLocaleString()}</TableCell>
              <TableCell className="text-right tabular-nums">
                {formatCurrency(row.revenueCents)}
              </TableCell>
              <TableCell className="text-right tabular-nums text-muted-foreground">
                {(row.share * 100).toFixed(1)}%
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {rows.length > 50 && (
        <p className="text-xs text-muted-foreground">
          Showing top 50 of {rows.length.toLocaleString()} SKUs, ranked by revenue.
        </p>
      )}
    </div>
  );
}

function SkuOrdersSheet({
  sku,
  orders,
  open,
  onOpenChange,
}: {
  sku: SalesSkuRow | null;
  orders: SalesDashOrder[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        {sku && (
          <>
            <SheetHeader>
              <SheetTitle>{sku.sku === 'NO-SKU' ? sku.name : sku.sku}</SheetTitle>
              <SheetDescription>
                {sku.name}
                {' · '}
                {sku.units.toLocaleString()} units across {sku.orders.toLocaleString()}{' '}
                {sku.orders === 1 ? 'order' : 'orders'}
                {' · '}
                {formatCurrency(sku.revenueCents)}
              </SheetDescription>
            </SheetHeader>
            <SheetBody className="space-y-3">
              {orders.length === 0 ? (
                <p className="py-6 text-center text-sm text-muted-foreground">
                  No orders for this SKU in the current filter.
                </p>
              ) : (
                orders.map((order) => {
                  const matching = order.lineItems.filter((li) => {
                    const key = `${order.channelKind}:${li.sku || 'NO-SKU'}:${li.name}`;
                    return key === sku.key;
                  });
                  const qty = matching.reduce((sum, li) => sum + li.quantity, 0);
                  const lineCents = matching.reduce(
                    (sum, li) => sum + li.quantity * li.unitPriceCents,
                    0,
                  );
                  return (
                    <div
                      key={order.id}
                      className="rounded-lg border border-border bg-card/40 p-3 text-sm"
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <ChannelKindBadge kind={order.channelKind} />
                          <span className="font-medium text-foreground">{order.soNumber}</span>
                        </div>
                        <span className="tabular-nums text-muted-foreground">
                          {formatDate(order.orderedAt)}
                        </span>
                      </div>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {order.customerName || order.customerEmail || 'No customer on file'}
                      </p>
                      <dl className="mt-2 grid grid-cols-3 gap-2 text-xs">
                        <div>
                          <dt className="text-muted-foreground">This SKU</dt>
                          <dd className="tabular-nums text-foreground">{qty} units</dd>
                        </div>
                        <div>
                          <dt className="text-muted-foreground">Line total</dt>
                          <dd className="tabular-nums text-foreground">
                            {formatCurrency(lineCents)}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-muted-foreground">Order total</dt>
                          <dd className="tabular-nums text-foreground">
                            {formatCurrency(order.totalCents)}
                          </dd>
                        </div>
                      </dl>
                    </div>
                  );
                })
              )}
            </SheetBody>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function ChannelKindBadge({ kind }: { kind: EcommerceKind | 'mixed' }) {
  if (kind === 'shopify') return <Badge variant="success">Shopify</Badge>;
  if (kind === 'amazon') return <Badge variant="warning">Amazon</Badge>;
  return <Badge variant="muted">Mixed</Badge>;
}

function pctSafe(current: number, previous: number): number | null {
  if (!Number.isFinite(current) || !Number.isFinite(previous) || previous === 0) return null;
  return (current - previous) / Math.abs(previous);
}

function safeShare(part: number, whole: number): number {
  if (!whole) return 0;
  return part / whole;
}
